import React from 'react';
import { ChevronDown, CheckCircle2, TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/format';
import type { RowPair } from './useOFXReconciliation';

interface DuplicatesAccordionProps {
  rows: RowPair[];
  open: boolean;
  onToggle: () => void;
}

function compactDate(date: string): string {
  const [, month, day] = date.split('-');
  return `${day}/${month}`;
}

export const DuplicatesAccordion: React.FC<DuplicatesAccordionProps> = ({
  rows,
  open,
  onToggle,
}) => {
  if (rows.length === 0) return null;

  return (
    <section className="bg-card border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-5 py-3 text-left hover:bg-muted/30 transition-colors"
      >
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform shrink-0',
            open && 'rotate-180',
          )}
        />
        <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground/70 shrink-0" aria-hidden="true" />
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Já importadas — {rows.length}
        </span>
        <span className="text-xs text-muted-foreground hidden sm:inline">· serão ignoradas</span>
      </button>

      {open && (
        <ul className="divide-y divide-border/50 animate-fade-in opacity-70">
          {rows.map((pair) => {
            const { row, fitid } = pair;
            const isIncome = row.type === 'income';
            return (
              <li key={fitid} className="px-5 py-2.5 flex items-center gap-3">
                <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap shrink-0 w-10">
                  {compactDate(row.date)}
                </span>
                <span
                  className={cn(
                    'inline-flex items-center justify-center h-5 w-5 rounded-full shrink-0',
                    isIncome ? 'bg-income/12 text-income' : 'bg-expense/12 text-expense',
                  )}
                  aria-label={isIncome ? 'Receita' : 'Despesa'}
                >
                  {isIncome ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : (
                    <TrendingDown className="h-3 w-3" />
                  )}
                </span>
                <span className="text-sm text-foreground/80 truncate min-w-0 flex-1">
                  {row.description}
                </span>
                <span
                  className={cn(
                    'text-sm tabular-nums whitespace-nowrap shrink-0',
                    isIncome ? 'text-income' : 'text-expense',
                  )}
                >
                  {formatCurrency(row.amount)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
